import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Stethoscope, Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState<{ factorId: string; challengeId: string } | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const navigate = useNavigate();

  const checkMfaAndProceed = async () => {
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal?.nextLevel === "aal2" && aal.currentLevel === "aal1") {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totp = (factors?.totp ?? []).find((f: any) => f.status === "verified");
      if (totp) {
        const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: totp.id });
        if (chErr) throw chErr;
        setMfa({ factorId: totp.id, challengeId: ch.id });
        return;
      }
    }
    toast.success("Welcome back!");
    navigate("/app");
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        await checkMfaAndProceed();
      } else {

        if (!firstName) {
          toast.error("Please enter your first name");
          setLoading(false);
          return;
        }
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { first_name: firstName, last_name: lastName },
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        toast.success("Account created! Check your email to verify.");
      }
    } catch (err: any) {
      toast.error(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Please enter your email first");
      return;
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success("Password reset email sent!");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12" style={{ background: "var(--gradient-sidebar)" }}>
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary-glow shadow-xl">
            <Stethoscope className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold text-black tracking-tight mb-3">DoctorsX</h1>
          <p className="text-sm text-black/60 uppercase tracking-widest mb-8">Doctor's Suite</p>
          <p className="text-lg text-black/80 leading-relaxed">
            Your all-in-one platform for managing patients, prescriptions, and appointments with ease.
          </p>
        </div>
      </div>

      {/* Right side - form */}
      <div className="flex flex-1 items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow shadow-lg">
              <Stethoscope className="h-6 w-6 text-primary-foreground" />
            </div>
            <h1 className="text-xl font-bold text-foreground">DoctorsX</h1>
          </div>

          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              {mfa ? "Two-factor code" : isLogin ? "Welcome back" : "Create account"}
            </h2>
            <p className="text-muted-foreground mt-2">
              {mfa ? "Enter the 6-digit code from your authenticator app" : isLogin ? "Sign in to access your dashboard" : "Register to get started with DoctorsX"}
            </p>
          </div>

          {mfa ? (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (mfaCode.length !== 6) { toast.error("Enter 6 digits"); return; }
                setLoading(true);
                try {
                  const { error } = await supabase.auth.mfa.verify({
                    factorId: mfa.factorId,
                    challengeId: mfa.challengeId,
                    code: mfaCode,
                  });
                  if (error) throw error;
                  toast.success("Welcome back!");
                  navigate("/app");
                } catch (err: any) {
                  toast.error(err.message || "Invalid code");
                  setMfaCode("");
                  // refresh challenge for next attempt
                  const { data: ch } = await supabase.auth.mfa.challenge({ factorId: mfa.factorId });
                  if (ch) setMfa({ ...mfa, challengeId: ch.id });
                } finally {
                  setLoading(false);
                }
              }}
              className="space-y-5"
            >
              <Input
                inputMode="numeric"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="tracking-[0.4em] text-center font-mono text-xl h-14 rounded-xl"
                autoFocus
              />
              <Button type="submit" disabled={loading || mfaCode.length !== 6} className="w-full h-11 bg-gradient-to-r from-primary to-primary-glow shadow-lg hover:shadow-xl transition-all text-base font-semibold rounded-xl">
                {loading ? "Verifying…" : "Verify"}
              </Button>
              <button
                type="button"
                onClick={async () => { await supabase.auth.signOut(); setMfa(null); setMfaCode(""); }}
                className="text-sm text-muted-foreground hover:text-foreground w-full text-center"
              >
                Cancel and sign out
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">

            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">First Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" className="pl-10 h-11 rounded-xl" />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Last Name</Label>
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Smith" className="h-11 rounded-xl" />
                </div>
              </div>
            )}

            <div className="grid gap-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="doctor@clinic.com" className="pl-10 h-11 rounded-xl" />
              </div>
            </div>

            <div className="grid gap-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-11 rounded-xl"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {isLogin && (
              <button type="button" onClick={handleForgotPassword} className="text-sm text-primary hover:underline font-medium">
                Forgot password?
              </button>
            )}

            <Button type="submit" disabled={loading} className="w-full h-11 bg-gradient-to-r from-primary to-primary-glow shadow-lg hover:shadow-xl transition-all text-base font-semibold rounded-xl">
              {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
            </Button>
          </form>
          )}

          {!mfa && (
            <p className="text-center text-sm text-muted-foreground">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
              <button onClick={() => setIsLogin(!isLogin)} className="text-primary font-semibold hover:underline">
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
